import React, { createContext, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import axios from 'axios';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

// Helpers
import { getSiteEndpoint } from 'helpers/api';
import {
  getMultipleContact,
  getNotification,
  getUnReadNotificationCount,
  loginApi,
  logoutApi,
  postMultipleContactId,
} from 'helpers/utilities';
import { loginRequest, msalConfig } from 'helpers/configuration';
import { COOKIES } from 'constants/cookieNames';

const customText = {
  ru: {
    alertBigPrivacy:
      'Этот сайт использует кукис и позволяет вам контролировать сервисы которые вы хотите активировать',
    denyAll: 'Запретить использование cookies',
    mandatoryTitle: 'Основные файлы cookie',
  },
  en: {
    alertBigPrivacy:
      'We are using cookies on this website to offer you a better browsing experience.&nbsp;Find out more here',
    denyAll: 'Essential Cookies only',
    mandatoryTitle: 'Essential Cookies',
  },
  fr: {
    alertBigPrivacy:
      'Ce site utilise des cookies et vous donne le contrôle sur ceux que vous souhaitez activer',
    denyAll: 'Tout refuser',
    mandatoryTitle: 'Cookies essentiels',
  },
  de: {
    alertBigPrivacy:
      "Diese Webseite verwendet 'Cookies' um Inhalte und Anzeigen zu personalisieren und zu analysieren. Bestimmen Sie, welche Dienste benutzt werden dürfen",
    denyAll: 'Verbiete alle Cookies',
    mandatoryTitle: 'Wesentliche Cookies',
  },
  hu: {
    alertBigPrivacy:
      'A weboldalunk sütik segítségével az Ön által kifejezetten óhajtott szolgáltatásokat biztosítani tudja - részletek',
    denyAll: 'Elutasítom',
    mandatoryTitle: 'nélkülözhetetlen sütiket',
  },
  no: {
    alertBigPrivacy:
      'Dette nettstedet bruker informasjonskapsler og gir deg kontroll over hva du vil aktivere',
    denyAll: 'Nekt alle informasjonskapsler',
    mandatoryTitle: 'Viktige informasjonskapsler',
  },

  da: {
    alertBigPrivacy:
      'Vi anvender cookies for at sikre dig den bedste oplevelse af vores hjemmeside – læs mere her',
    denyAll: 'Afvis alle cookies',
    mandatoryTitle: 'Væsentlige cookies',
  },
  es: {
    alertBigPrivacy: 'Este sitio web usa cookies y te permite controlar las que deseas activar',
    denyAll: 'Denegar todas las cookies',
    mandatoryTitle: 'Galletas Esenciales',
  },
  it: {
    alertBigPrivacy:
      "Questo sito utilizza i cookie per assicurarti la migliore esperienza di navigazione. Proseguendo la visita acconsenti all'utilizzo dei cookie da parte nostra. LEGGI DI PIU ",
    denyAll: 'Rifiuta tutti i cookie',
    mandatoryTitle: 'Cookie essenziali',
  },
  nl: {
    alertBigPrivacy:
      'Gebruik van cookiesOm de website zo optimaal mogelijk vorm te geven en voortdurend te kunnen verbeteren, maakt Kverneland Group gebruik van cookies. Lees meer',
    denyAll: 'Weiger alle cookies',
    mandatoryTitle: 'Essentiële cookies',
  },
  pl: {
    alertBigPrivacy: 'This site uses cookies and gives you control over what you want to activate',
    denyAll: 'Deny all cookies',
    mandatoryTitle: 'Essential Cookies',
  },
  sv: {
    alertBigPrivacy: 'Denna sida använder cookies och ger dig kontroll över vad du vill aktivera',
    denyAll: 'Neka ala čookieš',
    mandatoryTitle: 'Nevyhnutné súbory cookie',
  },
};

export const Auth = createContext();

const AuthContextProvider = ({ children }) => {
  const [lang, setLang] = useState(null);
  const [langFolder, setLangFolder] = useState(null);
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userToken, setUserToken] = useState(null);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(true);
  const [pageNotification, setPageNotification] = useState(1);
  const [notificationPageSize, setNotificationPageSize] = useState(20);
  const [stopLoadMore, setStopLoadMore] = useState(false);

  // Page state
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slugValue, setSlugValue] = useState(null);
  const [multipleContacData, setMultipleContacData] = useState([]);
  const isInitial = slugValue === null;
  const isRoot = slugValue === '';
  const isDeepLink = slugValue && slugValue !== '';

  const dashboardLink = Cookies.get(COOKIES.DASHBOARD);
  const timeoutInMiliseconds = 3600000;
  const timeoutIdRef = React.useRef(null);
  const [tokenFetched, setTokenFetched] = useState(false);
  const [navigateFunc, setNavigateFunc] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);

  // Check if this is the initial authentication or a subsequent page load
  const user = {
    hasAccessToken: false,
    accessToken: '',
    signIn: () => {
      sessionStorage.clear();
      instance.loginRedirect(loginRequest).catch((e) => {
        console.error(e);
      });
    },
    editProfile: () => {
      sessionStorage.clear();
      instance
        .loginRedirect({
          ...loginRequest,
          extraQueryParameters: { p: msalConfig.auth.policyEditProfile },
        })
        .catch((e) => {
          console.error(e);
        });
    },
    logout: () => {
      sessionStorage.clear();
      Cookies.remove(COOKIES.ACTIVE_USER_NAME);
      Cookies.remove(COOKIES.ACTIVE_USER_CONTACTS_COUNT);
      Cookies.remove(COOKIES.IS_FIRST_LOGIN);
      instance.logoutRedirect().catch((e) => {
        console.error(e);
      });
    },
    editPhoneNumber: () => {
      instance
        .loginRedirect({
          ...loginRequest,
          extraQueryParameters: { p: msalConfig.auth.policyEditPhoneNumber },
        })
        .catch((e) => {
          console.error(e);
        });
    },
    editPassword: () => {
      instance
        .loginRedirect({
          ...loginRequest,
          extraQueryParameters: { p: msalConfig.auth.policyEditPassword },
        })
        .catch((e) => {
          console.error(e);
        });
    },
  };

  const getNotificationsData = useCallback(
    async (token) => {
      await getUnReadNotificationCount(setNotificationCount, token);
      if (notifications.length === notificationPageSize || notifications.length === 0) {
        getNotification(
          setNotifications,
          token,
          setNotifLoading,
          pageNotification,
          setPageNotification,
          setStopLoadMore
        );
      }
    },
    [notifications.length, notificationPageSize, pageNotification, stopLoadMore]
  );

  const fetchData = useCallback(async (token, slug = '') => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${getSiteEndpoint()}/page/${slug}`, { headers });
      const pageRes = response?.data;
      setPage(pageRes);
      setLangFolder(pageRes?.langFolder);
      setLang(pageRes?.langFolder?.split('-')[1]);
    } catch (error) {
      const pageRes = error?.response?.data;
      if (pageRes) {
        setPage(pageRes);
        setLangFolder(pageRes?.langFolder);
        setLang(pageRes?.langFolder?.split('-')[1]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1. Wait until Page has set slugValue
    if (isInitial) {
      return;
    }

    // 2. If MSAL still working, wait
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // 3. Not authenticated → fetch anonymous page
    if (!isAuthenticated) {
      fetchData(undefined, slugValue || '');
      return;
    }

    // 4. Authenticated but token not ready → wait
    if (!userToken) {
      return;
    }

    // 5. Authenticated + token ready:
    //    If user came from "/" → go to dashboard
    if (isRoot && dashboardLink) {
      window.location.pathname = dashboardLink;
      return;
    }

    // 6. Deep link → fetch correct page
    fetchData(userToken, slugValue);
  }, [
    slugValue,
    isInitial,
    isRoot,
    isDeepLink,
    isAuthenticated,
    inProgress,
    userToken,
    dashboardLink,
    fetchData,
  ]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keypress', 'touchmove'];

    const resetTimer = () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        if (isAuthenticated === true) {
          user.logout();
        }
      }, timeoutInMiliseconds);
    };

    events.forEach((event) => {
      document.addEventListener(event, resetTimer, false);
    });

    resetTimer();
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer, false);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isAuthenticated]);

  const privacyUrls = {
    nl: 'https://legal.kubota-eu.com/nl/privacyverklaring-voor-gegevens/',
    fr: 'https://legal.kubota-eu.com/fr/avis-de-confidentialite-des-donnees/',
    zh: 'https://legal.kubota-eu.com/zh/%e6%95%b0%e6%8d%ae%e9%9a%90%e7%a7%81%e5%a3%b0%e6%98%8e/',
    da: 'https://legal.kubota-eu.com/da/databeskyttelseserklaering/',
    de: 'https://legal.kubota-eu.com/de/datenschutzhinweis/',
    it: 'https://legal.kubota-eu.com/it/avviso-sulla-privacy-dei-dati/',
    no: 'https://legal.kubota-eu.com/nb/personvernerklaering/',
    pl: 'https://legal.kubota-eu.com/pl/informacja-o-ochronie-danych/',
    ru: 'https://legal.kubota-eu.com/ru/%d1%83%d0%b2%d0%b5%d0%b4%d0%be%d0%bc%d0%bb%d0%b5%d0%bd%d0%b8%d0%b5-%d0%be-%d0%ba%d0%be%d0%bd%d1%84%d0%b8%d0%b4%d0%b5%d0%bd%d1%86%d0%b8%d0%b0%d0%bb%d1%8c%d0%bd%d0%be%d1%81%d1%82%d0%b8-%d0%b4%d0%b0/',
    es: 'https://legal.kubota-eu.com/es/aviso-de-privacidad-de-datos/',
    sv: 'https://legal.kubota-eu.com/sv/dataskyddsmeddelande/',
    en: 'https://legal.kubota-eu.com/data-privacy-notice/',
  };

  const tacLangRef = React.useRef(null);

  useEffect(() => {
    if (typeof window.tarteaucitron === 'undefined') {
      return;
    }

    // Anonymous users get the site - and the cookie banner - in English. The
    // real language only arrives later, when the user logs in or switches
    // dealer, and `lang` changes at that point.
    const _lang = lang || Cookies.get(COOKIES.CURRENT_LANG) || 'en';
    if (tacLangRef.current === _lang) {
      return;
    }
    const isFirstInit = tacLangRef.current === null;
    tacLangRef.current = _lang;

    Cookies.set(COOKIES.CURRENT_LANG, _lang);
    if (typeof window.tarteaucitronCustomText !== 'undefined') {
      window.tarteaucitronCustomText = customText[_lang];
    }
    window.tarteaucitronForceLanguage = _lang;
    const currentPrivacyUrl = privacyUrls[_lang] || privacyUrls['en'];

    window.tarteaucitron.init({
      privacyUrl: currentPrivacyUrl /* Privacy policy url */,
      hashtag: '#tarteaucitron' /* Open the panel with this hashtag */,
      cookieName: COOKIES.TARTEAUCITRON /* Cookie name */,
      orientation: 'middle' /* Banner position (top - bottom) */,
      groupServices: false /* Group services by category */,
      showAlertSmall: true /* Show the small banner on bottom right */,
      cookieslist: false /* Show the cookie list */,
      closePopup: false /* Show a close X on the banner */,

      showIcon: false /* Show cookie icon to manage cookies */,
      //"iconSrc": "", /* Optionnal: URL or base64 encoded image */
      iconPosition: 'BottomRight' /* BottomRight, BottomLeft, TopRight and TopLeft */,
      adblocker: false /* Show a Warning if an adblocker is detected */,
      DenyAllCta: true /* Show the deny all button */,
      AcceptAllCta: true /* Show the accept all button when highPrivacy on */,
      highPrivacy: true /* HIGHLY RECOMMANDED Disable auto consent */,
      handleBrowserDNTRequest: false /* If Do Not Track == 1, disallow all */,
      removeCredit: true /* Remove credit link */,
      moreInfoLink: true /* Show more info link */,
      useExternalCss: false /* If false, the tarteaucitron.css file will be loaded */,
      useExternalJs: false /* If false, the tarteaucitron.js file will be loaded */,
      //   "cookieDomain": "kverneland.com", /* Shared cookie for multisite */
      readmoreLink: currentPrivacyUrl /* Change the default readmore link */,
      mandatory: true /* Show a message about mandatory cookies */,
    });

    if (!isFirstInit) {
      // tarteaucitron builds its UI once, from the window "load" event, so the
      // banner is already on screen by the time the language changes. The
      // init() above replaced tarteaucitron.parameters, which is all the
      // "privacy policy" buttons need - they read the URL at click time - but
      // the per-service "read more" anchors had the previous URL baked into
      // their href when the panel was built, so refresh those here.
      document
        .querySelectorAll('#tarteaucitronRoot a.tarteaucitronReadmoreInfo')
        .forEach((anchor) => anchor.setAttribute('href', currentPrivacyUrl));
    }
  }, [lang]);

  const getAccessToken = useCallback(async () => {
    if (!instance) {
      console.error('MSAL instance is not initialized.');
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }

    if (instance) {
      const accessTokenRequest = { ...loginRequest, account: instance.getActiveAccount() };

      try {
        const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
        const accessToken = accessTokenResponse.accessToken;
        setUserToken(accessToken);

        // Login to backend
        const loginRes = await loginApi(accessToken);
        if (![200, 204].includes(loginRes.status)) {
          user.logout();
          logoutApi(accessToken);
          return;
        }

        // Fetch multiple contacts
        const contacts = await getMultipleContact(setMultipleContacData, accessToken);

        // Check if a contact is already selected
        const existingContact = Cookies.get(COOKIES.ACTIVE_USER_NAME);
        const currentPath = window.location.pathname;

        if (contacts?.length > 1) {
          // Multiple contacts
          Cookies.set(COOKIES.ACTIVE_USER_CONTACTS_COUNT, contacts.length);

          if (!existingContact && currentPath !== '/multiple-contact') {
            // No contact selected and not already on the page, redirect to selection page
            setRedirectTo('/multiple-contact');
            // Don't fetch notifications yet - wait until contact is selected
            return;
          } else if (existingContact) {
            // Contact is selected, tell backend which contact is active
            const selectedContact = contacts.find((c) => c.linkedAccount.accountNumber === existingContact);
            if (selectedContact) {
              try {
                await postMultipleContactId(selectedContact.id, accessToken);
              } catch (error) {
                console.error('[AuthContext getAccessToken] Error setting active contact:', error);
              }
            }

            if (currentPath === '/' && dashboardLink) {
              // Contact already selected, redirect from root to dashboard
              setRedirectTo('/dashboard');
            }
          }
          // After initial auth, don't auto-redirect to multiple-contact
          // Let the multiple-contact component itself handle guards
        } else if (contacts?.length === 1) {
          // Single contact, set cookies and tell backend
          const contact = contacts[0];
          Cookies.set(COOKIES.ACTIVE_USER_NAME, contact.linkedAccount.accountNumber);
          Cookies.set(COOKIES.ACTIVE_USER_CONTACTS_COUNT, 1);
          if (contact.langFolder) {
            Cookies.set(COOKIES.CURRENT_LANG, contact.langFolder.split('-').pop());
          }

          // Tell backend which contact is active
          try {
            await postMultipleContactId(contact.id, accessToken);
          } catch (error) {
            console.error('[AuthContext getAccessToken] Error setting active contact:', error);
          }

          if (currentPath === '/' && dashboardLink) {
            // Only redirect to dashboard from root
            setRedirectTo('/dashboard');
          }
        } else {
          // No contacts, logout
          user.logout();
          logoutApi(accessToken);
          return;
        }

        // Fetch notifications AFTER contact is confirmed/selected and backend knows which contact
        await getNotificationsData(accessToken);
      } catch (e) {
        if (e.errorCode === 'interaction_required') {
          instance.loginRedirect(loginRequest);
        } else {
          console.error('Token acquisition failed:', e);
          user.logout();
        }
      }
    }
  }, [instance, accounts, inProgress, dashboardLink, getNotificationsData]);

  const handleRedirection = useCallback(
    (slug, navigate) => {
      if (inProgress !== InteractionStatus.None) {
        // Authentication is still in progress, do nothing
        return;
      }

      if (!isAuthenticated) {
        // For anonymous users, set slug and fetch page
        setSlugValue(slug || '');
        fetchData(undefined, slug || '');
        return;
      }

      if (!userToken) {
        // Token is not ready, wait
        return;
      }

      if (slug === '' && dashboardLink) {
        // Root path, redirect to dashboard
        navigate(dashboardLink, { replace: true });
        return;
      }

      // Default: Fetch the page for the given slug
      fetchData(userToken, slug || '');
    },
    [inProgress, isAuthenticated, userToken, dashboardLink, fetchData]
  );

  useEffect(() => {
    const handleAuthentication = async () => {
      if (
        instance &&
        isAuthenticated &&
        accounts.length > 0 &&
        inProgress === InteractionStatus.None &&
        !tokenFetched
      ) {
        await getAccessToken();
        setTokenFetched(true);
      }
    };

    handleAuthentication();
  }, [instance, accounts, inProgress, tokenFetched, isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (redirectTo && navigateFunc) {
      navigateFunc(redirectTo, { replace: true });
      setRedirectTo(null);
    }
  }, [redirectTo, navigateFunc]);

  return (
    <Auth.Provider
      value={{
        // User-related methods
        handleLogin: () => user.signIn(),
        handleLogout: () => user.logout(),
        editProfile: () => user.editProfile(),
        editPhoneNumber: () => user.editPhoneNumber(),
        editPassword: () => user.editPassword(),

        // State setters
        setLang,
        setLangFolder,
        setUserToken,
        setPageNotification,
        setStopLoadMore,
        setNotificationPageSize,
        setNotifications,
        setNotificationCount,
        setNotifLoading,
        setSlugValue,
        setMultipleContacData,
        setNavigateFunc,

        // State variables
        langFolder,
        userToken,
        notifications,
        pageNotification,
        stopLoadMore,
        notificationPageSize,
        notificationCount,
        notifLoading,
        page,
        loading,
        multipleContacData,
        accounts,
        inProgress,

        // Utility methods
        getAccessToken: () => getAccessToken(),
        handleRedirection,
      }}
    >
      {children}
    </Auth.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.any,
};

export default AuthContextProvider;
